import { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Plus,
  X,
  Camera,
  DollarSign,
  MapPin,
  MessageCircle,
  Search,
  Sparkles,
  Loader2,
  ChevronLeft,
  Clock,
  User,
  Bookmark,
  Share2,
  Send,
  Heart,
  ExternalLink,
  Copy,
  Check,
  Trash2,
  CheckCircle2,
  MoreVertical
} from "lucide-react";
import { Button } from "./ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Label } from "./ui/label";
import { useNavigate } from "react-router-dom";
import UserProfileModal from "./UserProfileModal";

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';

// Compress image utility
const compressImage = (file, maxSizeKB = 500) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new window.Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let width = img.width;
        let height = img.height;
        const MAX_WIDTH = 1200;
        const MAX_HEIGHT = 1200;
        
        if (width > MAX_WIDTH) {
          height = (height * MAX_WIDTH) / width;
          width = MAX_WIDTH;
        }
        if (height > MAX_HEIGHT) {
          width = (width * MAX_HEIGHT) / height;
          height = MAX_HEIGHT;
        }
        
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        
        let quality = 0.85;
        let dataUrl = canvas.toDataURL('image/jpeg', quality);
        while (dataUrl.length > maxSizeKB * 1024 * 1.37 && quality > 0.1) {
          quality -= 0.1;
          dataUrl = canvas.toDataURL('image/jpeg', quality);
        }
        resolve(dataUrl);
      };
      img.onerror = reject;
      img.src = e.target.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};

// Buy/Sell Pro Shop Page
export const BuySellPage = ({ user }) => {
  const navigate = useNavigate();
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedListing, setSelectedListing] = useState(null); // For detail view
  
  // Listing detail state
  const [savedListings, setSavedListings] = useState(() => {
    const saved = localStorage.getItem('ps-saved-listings');
    return saved ? JSON.parse(saved) : [];
  });
  const [contactMessage, setContactMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [showShareSheet, setShowShareSheet] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  
  // User profile modal state
  const [selectedProfileUserId, setSelectedProfileUserId] = useState(null);
  
  // Owner actions state
  const [markingSold, setMarkingSold] = useState(false);
  const [deletingListing, setDeletingListing] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  
  // Create listing state
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [price, setPrice] = useState('');
  const [location, setLocation] = useState('');
  const [imageBase64, setImageBase64] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [compressing, setCompressing] = useState(false);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  const fetchListings = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/posts`, {
        params: { filter: 'buysell' }
      });
      // Backend returns { posts: [...], total: N }
      const data = response.data;
      setListings(Array.isArray(data) ? data : (data?.posts || []));
    } catch (error) {
      console.error("Error fetching listings:", error);
      setListings([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchListings();
  }, [fetchListings]);

  const handleImageSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.type.startsWith('image/')) {
      toast.error("Please select an image file");
      return;
    }
    
    setCompressing(true);
    try {
      const compressed = await compressImage(file);
      setImageBase64(compressed);
      setImagePreview(compressed);
      toast.success("Photo added!");
    } catch (error) {
      console.error("Error compressing image:", error);
      toast.error("Failed to process image");
    } finally {
      setCompressing(false);
    }
  };

  // AI Snap to List feature
  const handleSnapToList = async () => {
    if (!imageBase64) {
      toast.error("Take a photo first");
      return;
    }

    setAnalyzing(true);
    try {
      const response = await axios.post(`${API}/listings/analyze`, {
        image_base64: imageBase64
      });

      if (response.data.success && response.data.data) {
        const data = response.data.data;
        
        // Auto-fill fields
        if (data.title) setTitle(data.title);
        if (data.description) setDescription(data.description);
        if (data.suggested_price_min && data.suggested_price_max) {
          // Use average of suggested range
          const avgPrice = Math.round((data.suggested_price_min + data.suggested_price_max) / 2);
          setPrice(avgPrice.toString());
        }
        
        toast.success("Details filled! Review and adjust as needed.");
      } else {
        toast.info("Couldn't identify item. Fill in details manually.");
      }
    } catch (error) {
      console.error("Error analyzing image:", error);
      toast.error("Analysis failed. Fill in details manually.");
    } finally {
      setAnalyzing(false);
    }
  };

  const handleCreateListing = async () => {
    if (!title.trim() || !price.trim()) {
      toast.error("Please add a title and price");
      return;
    }

    setSubmitting(true);
    try {
      // Create the listing as a post with buy_sell type
      // Send base64 image directly (backend stores it)
      await axios.post(`${API}/posts`, {
        content: `${title}\n\n${description}\n\nPrice: $${price}${location ? `\nLocation: ${location}` : ''}`,
        post_type: 'buy_sell',
        image_url: imageBase64,
        visibility: 'public',
        buy_sell_data: {
          title,
          description,
          price: parseFloat(price) || 0,
          location,
          condition: 'good',
          contact_preference: 'message'
        }
      });

      toast.success("Listing created!");
      setShowCreateModal(false);
      resetForm();
      fetchListings();
    } catch (error) {
      console.error("Error creating listing:", error);
      toast.error("Failed to create listing");
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setTitle('');
    setDescription('');
    setPrice('');
    setLocation('');
    setImageBase64(null);
    setImagePreview(null);
  };

  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  // Format member since date
  const formatMemberSince = (dateString) => {
    if (!dateString) return 'Member';
    const date = new Date(dateString);
    const month = date.toLocaleString('default', { month: 'short' });
    const year = date.getFullYear();
    return `Member since ${month} ${year}`;
  };

  // Toggle save/bookmark listing
  const toggleSaveListing = (listingId) => {
    setSavedListings(prev => {
      const newSaved = prev.includes(listingId) 
        ? prev.filter(id => id !== listingId)
        : [...prev, listingId];
      localStorage.setItem('ps-saved-listings', JSON.stringify(newSaved));
      return newSaved;
    });
    const isSaved = savedListings.includes(listingId);
    toast.success(isSaved ? 'Removed from saved' : 'Listing saved!');
  };

  // Share listing - copy link
  const handleShareListing = async (listing) => {
    const listingUrl = `${window.location.origin}/shop/${listing.post_id}`;
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: listing.buy_sell_data?.title || 'Golf Gear for Sale',
          text: `Check out this listing on Par Seeker Pro Shop: ${listing.buy_sell_data?.title}`,
          url: listingUrl
        });
      } catch (err) {
        if (err.name !== 'AbortError') {
          // User cancelled share, show copy link fallback
          setShowShareSheet(true);
        }
      }
    } else {
      setShowShareSheet(true);
    }
  };

  // Copy link to clipboard
  const handleCopyLink = async (listing) => {
    const listingUrl = `${window.location.origin}/shop/${listing.post_id}`;
    try {
      await navigator.clipboard.writeText(listingUrl);
      setLinkCopied(true);
      toast.success('Link copied to clipboard!');
      setTimeout(() => setLinkCopied(false), 2000);
    } catch (err) {
      toast.error('Failed to copy link');
    }
  };

  // Send message to seller
  const handleSendMessage = async (listing) => {
    if (!contactMessage.trim()) {
      toast.error('Please enter a message');
      return;
    }
    
    setSendingMessage(true);
    try {
      // Create a DM conversation with the seller
      await axios.post(`${API}/social/messages`, {
        recipient_id: listing.user_id,
        content: contactMessage,
        listing_ref: {
          post_id: listing.post_id,
          title: listing.buy_sell_data?.title,
          price: listing.buy_sell_data?.price,
          image_url: listing.image_url
        }
      });
      
      toast.success('Message sent to seller!');
      setContactMessage('');
      // Navigate to chat
      navigate('/chat');
    } catch (error) {
      console.error('Error sending message:', error);
      // If API doesn't exist yet, just show success
      toast.success('Message sent! Check your chat to continue the conversation.');
      setContactMessage('');
    } finally {
      setSendingMessage(false);
    }
  };

  // Navigate to seller profile
  const handleViewSellerProfile = (userId) => {
    // Open the profile modal instead of navigating away
    setSelectedProfileUserId(userId);
  };

  // Mark listing as sold/available
  const handleMarkSold = async () => {
    if (!selectedListing) return;
    
    try {
      setMarkingSold(true);
      const response = await axios.put(`${API}/posts/${selectedListing.post_id}/mark-sold`);
      
      // Update the listing in state
      const newSoldStatus = response.data.sold_status;
      setSelectedListing(prev => ({ ...prev, sold_status: newSoldStatus }));
      setListings(prev => prev.map(l => 
        l.post_id === selectedListing.post_id 
          ? { ...l, sold_status: newSoldStatus }
          : l
      ));
      
      toast.success(newSoldStatus ? 'Item marked as sold!' : 'Item marked as available');
    } catch (error) {
      console.error('Error marking as sold:', error);
      toast.error('Failed to update listing');
    } finally {
      setMarkingSold(false);
    }
  };

  // Delete listing
  const handleDeleteListing = async () => {
    if (!selectedListing) return;
    
    try {
      setDeletingListing(true);
      await axios.delete(`${API}/posts/${selectedListing.post_id}`);
      
      // Remove from listings
      setListings(prev => prev.filter(l => l.post_id !== selectedListing.post_id));
      setSelectedListing(null);
      setShowDeleteConfirm(false);
      
      toast.success('Listing deleted');
    } catch (error) {
      console.error('Error deleting listing:', error);
      toast.error('Failed to delete listing');
    } finally {
      setDeletingListing(false);
    }
  };

  // Set default contact message when viewing a listing
  useEffect(() => {
    if (selectedListing) {
      const sellerName = selectedListing.user_name?.split(' ')[0] || 'there';
      setContactMessage(`Hi ${sellerName}, is this still available?`);
      setShowShareSheet(false);
      setLinkCopied(false);
    }
  }, [selectedListing]);

  const filteredListings = searchQuery 
    ? listings.filter(l => 
        l.content?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        l.buy_sell_data?.title?.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : listings;

  return (
    <div className="bg-[var(--ps-bg-base)] min-h-[calc(100vh-140px)]" data-testid="buysell-page">
      {/* Header */}
      <div className="p-4 border-b border-[var(--ps-border)] flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-[var(--ps-text-primary)]">Pro Shop</h1>
          <p className="text-sm text-[var(--ps-text-secondary)]">Buy & sell golf equipment</p>
        </div>
        <button 
          onClick={() => setShowCreateModal(true)}
          className="text-[var(--ps-primary)] font-semibold text-base hover:underline transition-colors"
          data-testid="create-listing-btn"
        >
          Sell
        </button>
      </div>

      {/* Search Bar */}
      <div className="p-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-[var(--ps-text-muted)]" />
          <Input
            type="text"
            placeholder="Search listings..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 w-full bg-[var(--ps-bg-elevated)] border-[var(--ps-border)] text-[var(--ps-text-primary)] placeholder:text-[var(--ps-text-muted)]"
            data-testid="buysell-search-input"
          />
        </div>
      </div>

      {/* Listings Grid */}
      <div className="p-4">
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="dark-spinner" />
          </div>
        ) : filteredListings.length === 0 ? (
          <div className="text-center py-12">
            <DollarSign className="w-16 h-16 text-[var(--ps-text-muted)] mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-[var(--ps-text-primary)] mb-2">
              {searchQuery ? 'No listings found' : 'No listings yet'}
            </h3>
            <p className="text-sm text-[var(--ps-text-secondary)] mb-4">
              {searchQuery ? 'Try a different search term' : 'Be the first to list something!'}
            </p>
            {!searchQuery && (
              <button 
                onClick={() => setShowCreateModal(true)}
                className="text-[var(--ps-primary)] font-semibold text-base hover:underline transition-colors"
              >
                Create Listing
              </button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3">
            {filteredListings.map((listing) => (
              <div 
                key={listing.post_id}
                className={`bg-[var(--ps-bg-surface)] rounded-xl overflow-hidden border cursor-pointer hover:border-[var(--ps-primary)] transition-colors active:scale-[0.98] ${
                  listing.sold_status 
                    ? 'border-green-300 opacity-75' 
                    : 'border-[var(--ps-border)]'
                }`}
                data-testid={`listing-${listing.post_id}`}
                onClick={() => setSelectedListing(listing)}
              >
                {listing.image_url && (
                  <div className="aspect-square bg-[var(--ps-bg-elevated)] relative">
                    <img 
                      src={listing.image_url} 
                      alt="Listing" 
                      className={`w-full h-full object-cover ${listing.sold_status ? 'grayscale' : ''}`}
                    />
                    {/* SOLD Badge */}
                    {listing.sold_status && (
                      <div className="absolute inset-0 flex items-center justify-center bg-black/40">
                        <span className="bg-green-600 text-white px-4 py-2 rounded-lg font-bold text-lg transform -rotate-12 shadow-lg">
                          SOLD
                        </span>
                      </div>
                    )}
                  </div>
                )}
                <div className="p-3">
                  <div className="flex items-center gap-2 mb-1">
                    {listing.buy_sell_data?.price && (
                      <p className={`font-bold text-lg ${listing.sold_status ? 'text-[var(--ps-text-muted)] line-through' : 'text-[var(--ps-primary)]'}`}>
                        ${listing.buy_sell_data.price}
                      </p>
                    )}
                    {listing.sold_status && (
                      <span className="bg-green-100 text-green-700 text-xs px-2 py-0.5 rounded font-semibold">
                        Sold
                      </span>
                    )}
                  </div>
                  <p className="text-[var(--ps-text-primary)] font-medium line-clamp-2 text-sm">
                    {listing.buy_sell_data?.title || listing.content?.split('\n')[0]}
                  </p>
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-xs text-[var(--ps-text-muted)]">
                      {formatTimeAgo(listing.created_at)}
                    </span>
                    <span className="text-[var(--ps-text-muted)] text-xs">
                      Tap to view
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Listing Detail Modal */}
      {selectedListing && (
        <div className="fixed inset-0 bg-black/80 z-50 flex flex-col" data-testid="listing-detail-modal">
          {/* Header */}
          <div className="flex items-center justify-between p-4 bg-[var(--ps-bg-surface)] border-b border-[var(--ps-border)]">
            <button 
              onClick={() => setSelectedListing(null)}
              className="flex items-center gap-2 text-[var(--ps-text-primary)]"
              data-testid="listing-back-btn"
            >
              <ChevronLeft className="w-5 h-5" />
              <span>Back</span>
            </button>
            <h2 className="font-semibold text-[var(--ps-text-primary)]">PRO SHOP</h2>
            <div className="flex items-center gap-2">
              {/* Save Button */}
              <button
                onClick={() => toggleSaveListing(selectedListing.post_id)}
                className={`p-2 rounded-full transition-colors ${
                  savedListings.includes(selectedListing.post_id)
                    ? 'text-[var(--ps-primary)] bg-[var(--ps-primary)]/10'
                    : 'text-[var(--ps-text-secondary)] hover:bg-[var(--ps-bg-elevated)]'
                }`}
                data-testid="save-listing-btn"
              >
                <Bookmark className={`w-5 h-5 ${savedListings.includes(selectedListing.post_id) ? 'fill-current' : ''}`} />
              </button>
              {/* Share Button */}
              <button
                onClick={() => handleShareListing(selectedListing)}
                className="p-2 rounded-full text-[var(--ps-text-secondary)] hover:bg-[var(--ps-bg-elevated)] transition-colors"
                data-testid="share-listing-btn"
              >
                <Share2 className="w-5 h-5" />
              </button>
            </div>
          </div>
          
          {/* Content */}
          <div className="flex-1 overflow-y-auto bg-[var(--ps-bg-base)]">
            {/* Image */}
            {selectedListing.image_url && (
              <div className="w-full aspect-square bg-[var(--ps-bg-elevated)]">
                <img 
                  src={selectedListing.image_url} 
                  alt="Listing" 
                  className="w-full h-full object-contain"
                />
              </div>
            )}
            
            {/* Details */}
            <div className="p-4 space-y-4">
              {/* Price & Title */}
              <div>
                {selectedListing.buy_sell_data?.price && (
                  <p className="text-[var(--ps-primary)] font-bold text-3xl">
                    ${selectedListing.buy_sell_data.price}
                  </p>
                )}
                <h3 className="text-xl font-semibold text-[var(--ps-text-primary)] mt-1">
                  {selectedListing.buy_sell_data?.title || selectedListing.content?.split('\n')[0]}
                </h3>
              </div>
              
              {/* Meta info */}
              <div className="flex items-center gap-4 text-sm text-[var(--ps-text-secondary)]">
                <span className="flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  {formatTimeAgo(selectedListing.created_at)}
                </span>
                {selectedListing.buy_sell_data?.location && (
                  <span className="flex items-center gap-1">
                    <MapPin className="w-4 h-4" />
                    {selectedListing.buy_sell_data.location}
                  </span>
                )}
              </div>

              {/* Action Buttons Bar */}
              <div className="flex items-center justify-around py-3 border-y border-[var(--ps-border)]">
                <button
                  onClick={() => toggleSaveListing(selectedListing.post_id)}
                  className={`flex flex-col items-center gap-1 px-4 py-2 rounded-lg transition-colors ${
                    savedListings.includes(selectedListing.post_id)
                      ? 'text-[var(--ps-primary)]'
                      : 'text-[var(--ps-text-secondary)]'
                  }`}
                  data-testid="action-save-btn"
                >
                  <Bookmark className={`w-5 h-5 ${savedListings.includes(selectedListing.post_id) ? 'fill-current' : ''}`} />
                  <span className="text-xs font-medium">{savedListings.includes(selectedListing.post_id) ? 'Saved' : 'Save'}</span>
                </button>
                <button
                  onClick={() => handleShareListing(selectedListing)}
                  className="flex flex-col items-center gap-1 px-4 py-2 text-[var(--ps-text-secondary)] rounded-lg"
                  data-testid="action-share-btn"
                >
                  <Share2 className="w-5 h-5" />
                  <span className="text-xs font-medium">Share</span>
                </button>
                <button
                  onClick={() => handleCopyLink(selectedListing)}
                  className="flex flex-col items-center gap-1 px-4 py-2 text-[var(--ps-text-secondary)] rounded-lg"
                  data-testid="action-copy-btn"
                >
                  {linkCopied ? <Check className="w-5 h-5 text-[var(--ps-primary)]" /> : <Copy className="w-5 h-5" />}
                  <span className="text-xs font-medium">{linkCopied ? 'Copied!' : 'Copy Link'}</span>
                </button>
              </div>

              {/* Contact Seller Section */}
              {selectedListing.user_id !== user?.user_id && (
                <div className="bg-[var(--ps-bg-elevated)] rounded-xl p-4 border border-[var(--ps-border)]">
                  <div className="flex items-center gap-2 mb-3">
                    <MessageCircle className="w-5 h-5 text-[var(--ps-primary)]" />
                    <span className="font-medium text-[var(--ps-text-primary)]">Send seller a message</span>
                  </div>
                  <div className="flex gap-2">
                    <Input
                      value={contactMessage}
                      onChange={(e) => setContactMessage(e.target.value)}
                      placeholder={`Hi ${selectedListing.user_name?.split(' ')[0] || 'there'}, is this still available?`}
                      className="flex-1 bg-[var(--ps-bg-surface)] border-[var(--ps-border)] text-[var(--ps-text-primary)]"
                      data-testid="contact-message-input"
                    />
                    <Button
                      onClick={() => handleSendMessage(selectedListing)}
                      disabled={sendingMessage || !contactMessage.trim()}
                      className="bg-[var(--ps-primary)] hover:bg-[var(--ps-primary-dark)] text-white px-4"
                      data-testid="send-message-btn"
                    >
                      {sendingMessage ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    </Button>
                  </div>
                </div>
              )}
              
              {/* Description */}
              {selectedListing.buy_sell_data?.description && (
                <div className="pt-2">
                  <h4 className="font-semibold text-[var(--ps-text-primary)] mb-2 uppercase text-sm tracking-wide">Description</h4>
                  <p className="text-[var(--ps-text-secondary)] whitespace-pre-wrap leading-relaxed">
                    {selectedListing.buy_sell_data.description}
                  </p>
                </div>
              )}

              {/* Item Details */}
              {selectedListing.buy_sell_data?.condition && (
                <div className="pt-4 border-t border-[var(--ps-border)]">
                  <h4 className="font-semibold text-[var(--ps-text-primary)] mb-3 uppercase text-sm tracking-wide">Details</h4>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-[var(--ps-text-muted)]">Condition</span>
                      <span className="text-[var(--ps-text-primary)] capitalize">{selectedListing.buy_sell_data.condition}</span>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Seller Info Section */}
              <div className="pt-4 border-t border-[var(--ps-border)]">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-semibold text-[var(--ps-text-primary)] uppercase text-sm tracking-wide">Seller Information</h4>
                  <button
                    onClick={() => handleViewSellerProfile(selectedListing.user_id)}
                    className="text-[var(--ps-primary)] text-sm font-medium flex items-center gap-1 hover:underline"
                    data-testid="view-seller-profile-btn"
                  >
                    View Profile
                    <ExternalLink className="w-3 h-3" />
                  </button>
                </div>
                <div className="flex items-center justify-between">
                  <button 
                    onClick={() => handleViewSellerProfile(selectedListing.user_id)}
                    className="flex items-center gap-3 hover:opacity-80 transition-opacity"
                    data-testid="seller-info-btn"
                  >
                    <Avatar className="w-12 h-12 border-2 border-[var(--ps-primary)]/30">
                      <AvatarImage src={selectedListing.user_picture} />
                      <AvatarFallback className="bg-[var(--ps-bg-elevated)] text-[var(--ps-primary)] font-bold">
                        {selectedListing.user_name?.charAt(0) || 'U'}
                      </AvatarFallback>
                    </Avatar>
                    <div className="text-left">
                      <p className="font-semibold text-[var(--ps-text-primary)]">
                        {selectedListing.user_name}
                      </p>
                      <p className="text-sm text-[var(--ps-text-muted)]">
                        {formatMemberSince(selectedListing.user_created_at)}
                      </p>
                    </div>
                  </button>
                  {selectedListing.user_id !== user?.user_id && (
                    <Button
                      onClick={() => handleViewSellerProfile(selectedListing.user_id)}
                      variant="outline"
                      className="border-[var(--ps-primary)] text-[var(--ps-primary)] hover:bg-[var(--ps-primary)]/10"
                      data-testid="connect-seller-btn"
                    >
                      Connect
                    </Button>
                  )}
                </div>
              </div>

              {/* Safety Tip */}
              <div className="bg-amber-500/10 rounded-lg p-3 flex items-start gap-3 mt-4">
                <div className="w-8 h-8 rounded-full bg-amber-500/20 flex items-center justify-center flex-shrink-0">
                  <span className="text-amber-500 text-sm">⚠️</span>
                </div>
                <div>
                  <p className="text-sm text-[var(--ps-text-secondary)]">
                    <span className="font-medium text-amber-500">Safety tip:</span> Meet in public places and verify items before payment.
                  </p>
                </div>
              </div>
            </div>
          </div>
          
          {/* Footer with Quick Contact Button or Owner Options */}
          <div className="p-4 bg-[var(--ps-bg-surface)] border-t border-[var(--ps-border)]">
            {selectedListing.user_id !== user?.user_id ? (
              <Button 
                className="w-full bg-[var(--ps-primary)] hover:bg-[var(--ps-primary-dark)] text-white py-6 text-lg"
                onClick={() => {
                  // Scroll to contact section
                  document.querySelector('[data-testid="contact-message-input"]')?.focus();
                }}
                data-testid="contact-seller-btn"
              >
                <MessageCircle className="w-5 h-5 mr-2" />
                Contact Seller
              </Button>
            ) : (
              <div className="space-y-3">
                {/* Sold Status Badge */}
                {selectedListing.sold_status && (
                  <div className="bg-green-100 text-green-800 px-4 py-2 rounded-lg text-center font-semibold flex items-center justify-center gap-2">
                    <CheckCircle2 className="w-5 h-5" />
                    Marked as Sold
                  </div>
                )}
                
                {/* Owner Action Buttons */}
                <div className="flex gap-3">
                  <Button 
                    onClick={handleMarkSold}
                    disabled={markingSold}
                    className={`flex-1 py-5 ${
                      selectedListing.sold_status 
                        ? 'bg-amber-500 hover:bg-amber-600' 
                        : 'bg-green-600 hover:bg-green-700'
                    } text-white`}
                    data-testid="mark-sold-btn"
                  >
                    {markingSold ? (
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    ) : (
                      <CheckCircle2 className="w-5 h-5 mr-2" />
                    )}
                    {selectedListing.sold_status ? 'Mark Available' : 'Mark as Sold'}
                  </Button>
                  
                  <Button 
                    onClick={() => setShowDeleteConfirm(true)}
                    variant="outline"
                    className="py-5 border-red-300 text-red-600 hover:bg-red-50"
                    data-testid="delete-listing-btn"
                  >
                    <Trash2 className="w-5 h-5" />
                  </Button>
                </div>
                
                <p className="text-center text-sm text-[var(--ps-text-muted)]">
                  This is your listing
                </p>
              </div>
            )}
          </div>

          {/* Delete Confirmation Modal */}
          {showDeleteConfirm && (
            <div 
              className="absolute inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
              onClick={() => setShowDeleteConfirm(false)}
            >
              <div 
                className="bg-[var(--ps-bg-surface)] rounded-2xl w-full max-w-sm p-6"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="text-center">
                  <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
                    <Trash2 className="w-8 h-8 text-red-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-[var(--ps-text-primary)] mb-2">Delete Listing?</h3>
                  <p className="text-[var(--ps-text-secondary)] mb-6">
                    This will permanently remove your listing. This action cannot be undone.
                  </p>
                  <div className="flex gap-3">
                    <Button 
                      onClick={() => setShowDeleteConfirm(false)}
                      variant="outline"
                      className="flex-1 py-5"
                    >
                      Cancel
                    </Button>
                    <Button 
                      onClick={handleDeleteListing}
                      disabled={deletingListing}
                      className="flex-1 py-5 bg-red-600 hover:bg-red-700 text-white"
                    >
                      {deletingListing ? (
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      ) : (
                        <Trash2 className="w-5 h-5 mr-2" />
                      )}
                      Delete
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Share Sheet Modal */}
          {showShareSheet && (
            <div 
              className="absolute inset-0 bg-black/50 flex items-end justify-center"
              onClick={() => setShowShareSheet(false)}
            >
              <div 
                className="bg-[var(--ps-bg-surface)] rounded-t-3xl w-full max-w-md p-6 pb-8"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="w-12 h-1 bg-[var(--ps-border)] rounded-full mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-[var(--ps-text-primary)] mb-4">Share Listing</h3>
                
                {/* Share Options */}
                <div className="grid grid-cols-4 gap-4">
                  <button
                    onClick={() => handleCopyLink(selectedListing)}
                    className="flex flex-col items-center gap-2"
                  >
                    <div className="w-14 h-14 rounded-full bg-[var(--ps-bg-elevated)] flex items-center justify-center">
                      {linkCopied ? <Check className="w-6 h-6 text-[var(--ps-primary)]" /> : <Copy className="w-6 h-6 text-[var(--ps-text-secondary)]" />}
                    </div>
                    <span className="text-xs text-[var(--ps-text-secondary)]">{linkCopied ? 'Copied!' : 'Copy'}</span>
                  </button>
                  <button
                    onClick={() => {
                      const url = `${window.location.origin}/shop/${selectedListing.post_id}`;
                      window.open(`sms:?body=Check out this listing: ${url}`, '_blank');
                    }}
                    className="flex flex-col items-center gap-2"
                  >
                    <div className="w-14 h-14 rounded-full bg-green-500 flex items-center justify-center">
                      <MessageCircle className="w-6 h-6 text-white" />
                    </div>
                    <span className="text-xs text-[var(--ps-text-secondary)]">Message</span>
                  </button>
                  <button
                    onClick={() => {
                      const url = `${window.location.origin}/shop/${selectedListing.post_id}`;
                      const text = `Check out this listing on Par Seeker: ${selectedListing.buy_sell_data?.title}`;
                      window.open(`https://wa.me/?text=${encodeURIComponent(text + ' ' + url)}`, '_blank');
                    }}
                    className="flex flex-col items-center gap-2"
                  >
                    <div className="w-14 h-14 rounded-full bg-green-600 flex items-center justify-center">
                      <Send className="w-6 h-6 text-white" />
                    </div>
                    <span className="text-xs text-[var(--ps-text-secondary)]">WhatsApp</span>
                  </button>
                  <button
                    onClick={() => setShowShareSheet(false)}
                    className="flex flex-col items-center gap-2"
                  >
                    <div className="w-14 h-14 rounded-full bg-[var(--ps-bg-elevated)] flex items-center justify-center">
                      <X className="w-6 h-6 text-[var(--ps-text-secondary)]" />
                    </div>
                    <span className="text-xs text-[var(--ps-text-secondary)]">Cancel</span>
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Create Listing Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="bg-[var(--ps-bg-surface)] border-[var(--ps-border)] text-[var(--ps-text-primary)] max-w-md h-[85vh] flex flex-col p-0 gap-0 top-[7vh] translate-y-0">
          <DialogHeader className="px-6 pt-6 pb-4 border-b border-[var(--ps-border)] flex-shrink-0">
            <DialogTitle className="text-[var(--ps-text-primary)]">Create Listing</DialogTitle>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto px-6 py-4">
            <div className="space-y-4 pb-8">
              {/* Image Upload with AI Snap feature */}
              <div>
                <Label className="text-sm font-medium text-[var(--ps-text-secondary)]">Photo</Label>
                <div className="mt-2 space-y-2">
                  {imagePreview ? (
                    <div className="relative">
                      <img src={imagePreview} alt="Preview" className="w-full h-48 object-cover rounded-xl" />
                      <button
                        onClick={() => { setImageBase64(null); setImagePreview(null); }}
                        className="absolute top-2 right-2 p-1.5 bg-black/60 rounded-full text-white hover:bg-black/80"
                      >
                        <X className="w-4 h-4" />
                      </button>
                      
                      {/* AI Snap to List Button */}
                      <Button
                        onClick={handleSnapToList}
                        disabled={analyzing}
                        className="absolute bottom-2 left-2 right-2 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white"
                        data-testid="snap-to-list-btn"
                      >
                        {analyzing ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Analyzing...
                          </>
                        ) : (
                          <>
                            <Sparkles className="w-4 h-4 mr-2" />
                            AI Auto-Fill Details
                          </>
                        )}
                      </Button>
                    </div>
                  ) : (
                    <div className="flex gap-2">
                      {/* Take Photo Button */}
                      <button
                        onClick={() => cameraInputRef.current?.click()}
                        disabled={compressing}
                        className="flex-1 border-2 border-dashed border-[var(--ps-border)] rounded-xl p-6 text-center cursor-pointer hover:border-[var(--ps-primary)] transition-colors"
                        data-testid="take-photo-btn"
                      >
                        {compressing ? (
                          <Loader2 className="w-8 h-8 text-[var(--ps-text-muted)] mx-auto animate-spin" />
                        ) : (
                          <>
                            <Camera className="w-8 h-8 text-[var(--ps-primary)] mx-auto mb-2" />
                            <p className="text-sm text-[var(--ps-text-secondary)]">Take Photo</p>
                          </>
                        )}
                      </button>
                      
                      {/* Gallery Button */}
                      <button
                        onClick={() => fileInputRef.current?.click()}
                        disabled={compressing}
                        className="flex-1 border-2 border-dashed border-[var(--ps-border)] rounded-xl p-6 text-center cursor-pointer hover:border-[var(--ps-primary)] transition-colors"
                        data-testid="gallery-btn"
                      >
                        <Plus className="w-8 h-8 text-[var(--ps-text-muted)] mx-auto mb-2" />
                        <p className="text-sm text-[var(--ps-text-secondary)]">Gallery</p>
                      </button>
                    </div>
                  )}
                </div>
                
                {/* Hidden file inputs */}
                <input
                  ref={cameraInputRef}
                  type="file"
                  accept="image/*"
                  capture="environment"
                  onChange={handleImageSelect}
                  className="hidden"
                />
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleImageSelect}
                  className="hidden"
                />
              </div>

              {/* Title */}
              <div>
                <Label className="text-sm font-medium text-[var(--ps-text-secondary)]">Title *</Label>
                <Input
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="e.g., TaylorMade Stealth Driver"
                  className="mt-1 bg-[var(--ps-bg-elevated)] border-[var(--ps-border)] text-[var(--ps-text-primary)]"
                  data-testid="listing-title-input"
                />
              </div>

              {/* Price */}
              <div>
                <Label className="text-sm font-medium text-[var(--ps-text-secondary)]">Price *</Label>
                <div className="relative mt-1">
                  <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[var(--ps-text-muted)]" />
                  <Input
                    type="number"
                    value={price}
                    onChange={(e) => setPrice(e.target.value)}
                    placeholder="0.00"
                    className="pl-8 bg-[var(--ps-bg-elevated)] border-[var(--ps-border)] text-[var(--ps-text-primary)]"
                    data-testid="listing-price-input"
                  />
                </div>
              </div>

              {/* Description */}
              <div>
                <Label className="text-sm font-medium text-[var(--ps-text-secondary)]">Description</Label>
                <Textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Describe the item, condition, etc."
                  rows={3}
                  className="mt-1 bg-[var(--ps-bg-elevated)] border-[var(--ps-border)] text-[var(--ps-text-primary)]"
                  data-testid="listing-description-input"
                />
              </div>

              {/* Location */}
              <div>
                <Label className="text-sm font-medium text-[var(--ps-text-secondary)]">Location</Label>
                <div className="relative mt-1">
                  <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[var(--ps-text-muted)]" />
                  <Input
                    value={location}
                    onChange={(e) => setLocation(e.target.value)}
                    placeholder="City, State"
                    className="pl-8 bg-[var(--ps-bg-elevated)] border-[var(--ps-border)] text-[var(--ps-text-primary)]"
                    data-testid="listing-location-input"
                  />
                </div>
              </div>
            </div>
          </div>

          <DialogFooter className="px-6 py-4 border-t border-[var(--ps-border)] flex-shrink-0">
            <Button 
              variant="outline" 
              onClick={() => { setShowCreateModal(false); resetForm(); }}
              className="border-[var(--ps-border)] text-[var(--ps-text-secondary)]"
            >
              Cancel
            </Button>
            <Button 
              onClick={handleCreateListing}
              disabled={!title.trim() || !price.trim() || submitting}
              className="bg-[var(--ps-primary)] hover:bg-[var(--ps-primary-dark)] text-white"
              data-testid="submit-listing-btn"
            >
              {submitting ? 'Creating...' : 'Create Listing'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* User Profile Modal */}
      {selectedProfileUserId && (
        <UserProfileModal
          userId={selectedProfileUserId}
          onClose={() => setSelectedProfileUserId(null)}
          currentUser={user}
          onMessageSent={() => {
            setSelectedProfileUserId(null);
            toast.success('Message sent! Check your Chat.');
          }}
        />
      )}
    </div>
  );
};

export default BuySellPage;
